/**
 * AnchorLinks Component
 *
 * Custom component for displaying anchor links as a horizontal swiper.
 * Used in ShowroomDetails for the relatedPages field.
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';

import {
  Box,
  Link as ChakraLink,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/base/app/components/shared/swiper';
import {ArrowBottomRightIcon} from '@amblique/king-living/app/components/icons';

/**
 * Smooth scroll to element with offset for header
 * Uses a larger offset to account for sticky header on all screen sizes
 */
const scrollToElement = (elementId, offset = 150) => {
  const element = document.getElementById(elementId);

  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

/**
 * Single anchor link item component
 */
const AnchorLinkItem = ({link, linkStyles}) => {
  const isExternal = link.url?.startsWith('http') || link.openInNewTab;
  const isHashLink = link.url?.startsWith('#');
  const key = link.sys?.id || link.url || link.text;

  const content = (
    <>
      {link.text}
      <ArrowBottomRightIcon boxSize={{base: '14px', lg: '16px'}} />
    </>
  );

  // Handle hash links with smooth scrolling
  const handleHashClick = (e) => {
    e.preventDefault();

    const elementId = link.url.substring(1); // Remove the # prefix

    scrollToElement(elementId);

    // Update URL hash without jumping
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', link.url);
    }
  };

  if (isHashLink) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        {...linkStyles}
        onClick={handleHashClick}
      >
        {content}
      </ChakraLink>
    );
  }

  if (isExternal) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        isExternal={link.openInNewTab}
        {...linkStyles}
      >
        {content}
      </ChakraLink>
    );
  }

  return (
    <ChakraLink
      key={key}
      as={Link}
      to={link.url}
      {...linkStyles}
    >
      {content}
    </ChakraLink>
  );
};

AnchorLinkItem.propTypes = {
  link: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    openInNewTab: PropTypes.bool,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  linkStyles: PropTypes.object,
};

export const AnchorLinks = ({links = [], containerProps = {}, ...props}) => {
  const styles = useMultiStyleConfig('contentful/showroom-details');

  const swiperOptions = useMemo(() => ({
    navigation: true,
    pagination: false,
    scrollbar: false,
    autoHeight: false,
    spaceBetween: '16px',
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    freeMode: true,
  }), []);

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <Box
      className="anchor-links-container"
      position="relative"
      {...containerProps}
      {...props}
    >
      <Box {...styles.anchorLinksSwiperWrapper}>
        <Swiper
          className="anchor-links-swiper"
          options={swiperOptions}
          presets=""
          {...styles.anchorLinksSwiper}
        >
          {links.map((link) => (
            <Box key={link.sys?.id || link.url || link.text}>
              <AnchorLinkItem link={link} linkStyles={styles.anchorLinksItem} />
            </Box>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
};

AnchorLinks.displayName = 'AnchorLinks';

AnchorLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      openInNewTab: PropTypes.bool,
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
    })
  ),
  containerProps: PropTypes.object,
};

export default AnchorLinks;
